import React from 'react';
import { RefreshCw } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import { stateIcon, stateTone } from '../../utils/stateIcons';
import './PageEmptyState.css';

/**
 * PageEmptyState — the empty / error state for a page or a whole section (spec §3.12).
 * An icon on a tone chip says what kind of thing is missing; the text always says what is
 * missing and the next step, calmly (no "Oops!").
 *
 *   <PageEmptyState
 *     variant="search"
 *     title="Hech narsa topilmadi"
 *     description="Boshqa so'z bilan qidirib ko'ring yoki filtrlarni tozalang."
 *     actionLabel="Filtrlarni tozalash"
 *     onAction={resetFilters}
 *   />
 *   <PageEmptyState variant="error" onRetry={load} />   // "Xatolik yuz berdi" + ink "Qaytadan" button
 *
 * `variant` picks the icon (override with `kind`, one of utils/stateIcons):
 *   default / inbox / revenue -> empty (nothing yet) · search -> search · students -> students
 *   calendar -> calendar · docs -> docs · tickets -> support · forbidden -> lock · error -> error
 *   success -> success
 * `size`: 'md' (default card, min-height 300), 'sm' (a panel inside a page, 220), 'lg' (a whole
 * page, no card). For a box among other boxes use <InlineEmpty> instead.
 * `icon` (an element) replaces the kind's icon. `children` render between the text and
 * the buttons. Never pass `err.message` as the description: route API errors through t('errors.*').
 */

const KINDS = {
  default: 'empty',
  search: 'search',
  inbox: 'empty',
  revenue: 'empty',
  students: 'students',
  calendar: 'calendar',
  docs: 'docs',
  tickets: 'support',
  forbidden: 'lock',
  error: 'error',
  success: 'success',
};

function PageEmptyState({
  title,
  description,
  actionLabel,
  onAction,
  secondaryLabel,
  onSecondaryAction,
  onRetry,
  icon,
  kind,
  variant = 'default',
  size = 'md',
  children,
}) {
  const { t } = useI18n();
  const isError = variant === 'error';
  const resolvedTitle = title ?? (isError ? t('ui.errorTitle') : variant === 'forbidden' ? t('ui.forbiddenTitle') : undefined);
  const resolvedDescription = description ?? (isError ? t('ui.errorDescription') : variant === 'forbidden' ? t('ui.forbiddenDescription') : undefined);
  const kindName = kind || KINDS[variant] || KINDS.default;

  const hasPrimary = actionLabel && onAction;
  const hasSecondary = secondaryLabel && onSecondaryAction;
  const hasRetry = isError && onRetry && !hasPrimary;

  return (
    <div
      className={`page-empty-state pes-${size} pes-variant-${variant}`}
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
    >
      <div className="pes-media">
        <div className="pes-icon" data-tone={stateTone(kindName, 'brand')}>{icon || stateIcon(kindName)}</div>
      </div>
      {resolvedTitle && <h2 className="pes-title">{resolvedTitle}</h2>}
      {resolvedDescription && <p className="pes-desc">{resolvedDescription}</p>}
      {children && <div className="pes-body">{children}</div>}
      {(hasPrimary || hasSecondary || hasRetry) && (
        <div className="pes-actions">
          {hasRetry && (
            <button type="button" onClick={onRetry} className="ds-btn ds-btn-ink">
              <RefreshCw aria-hidden="true" />
              {t('ui.retryAgain')}
            </button>
          )}
          {hasPrimary && (
            <button
              type="button"
              onClick={onAction}
              className={`ds-btn ${isError ? 'ds-btn-ink' : 'ds-btn-primary'}`}
            >
              {isError && <RefreshCw aria-hidden="true" />}
              {actionLabel}
            </button>
          )}
          {hasSecondary && (
            <button type="button" onClick={onSecondaryAction} className="ds-btn ds-btn-secondary">
              {secondaryLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default PageEmptyState;
